import React, { useEffect, useState } from "react";
import {
  IoGlobeOutline,
  IoStorefrontOutline,
  IoDocumentOutline,
  IoDocumentTextOutline,
  IoTrashOutline,
  IoRefreshOutline,
} from "react-icons/io5";
import KnowledgeStatusPill from "./KnowledgeStatusPill";
import { _botKnowledgeStatus } from "Services/api";
import { KnowledgeSourceType } from "constants/react-hook-form";

// Same cadence the legacy single-source poller used.
const TASK_STATUS_POLL_INTERVAL_MS = 4000;

// Retry only makes sense for sources the backend can re-scrape from a URL.
const RETRYABLE_TYPES = new Set([KnowledgeSourceType.WEBSITE, "plist_storefront"]);

const SOURCE_TYPE_META = {
  plist_storefront: { label: "Plist storefront", Icon: IoStorefrontOutline },
  website: { label: "Website", Icon: IoGlobeOutline },
  pdf: { label: "PDF", Icon: IoDocumentOutline },
  manual: { label: "Manual text", Icon: IoDocumentTextOutline },
};

const isTerminalStatus = (status) => status === "completed" || status === "failed";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1000) return `${bytes} B`;
  return `${(bytes / 1000).toFixed(bytes < 100000 ? 1 : 0)} KB`;
};

/**
 * A single knowledge source row. The authoritative lifecycle `status` comes
 * from the source object (kept fresh by the panel's list-poll); this component
 * additionally polls `/task-status` for a live progress % when the panel handed
 * it a `taskId` from an add/retry performed in the current session.
 */
const KnowledgeSourceItem = ({
  source,
  taskId,
  accessToken,
  onRetry,
  onRemove,
  onTaskComplete,
  isRetrying,
  isRemoving,
}) => {
  const [progress, setProgress] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);

  const status = source?.status || null;
  const sourceId = source?.id ?? source?.source_id;
  const sourceType = source?.source_type;
  const meta = SOURCE_TYPE_META[sourceType] || {
    label: sourceType || "Source",
    Icon: IoDocumentOutline,
  };
  const { Icon } = meta;
  const isAuto = source?.origin === "auto";
  const canRetry = status === "failed" && RETRYABLE_TYPES.has(sourceType);
  const label = source?.title || source?.ref || meta.label;

  // Poll task status for progress while this source is still working and we
  // have a task id for it. When task-status itself reports a terminal state we
  // stop polling and tell the panel to refetch the list once — the list is
  // otherwise not polled while a task id is being tracked.
  useEffect(() => {
    if (!taskId || isTerminalStatus(status)) {
      return undefined;
    }

    let cancelled = false;
    let intervalId;

    const stop = () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };

    const poll = async () => {
      try {
        const response = await _botKnowledgeStatus(accessToken, taskId);
        if (cancelled) return;
        setProgress(response?.data?.progress ?? null);
        setTaskDetail(response?.data || null);
        if (isTerminalStatus(response?.data?.status)) {
          stop();
          onTaskComplete?.(sourceId);
        }
      } catch (error) {
        // A 503 means task-status storage is briefly unavailable; keep polling.
        console.error("Error polling knowledge source status:", error);
      }
    };

    poll();
    intervalId = setInterval(poll, TASK_STATUS_POLL_INTERVAL_MS);

    return () => {
      stop();
    };
  }, [taskId, status, accessToken, onTaskComplete, sourceId]);

  const listingsDiscovered = taskDetail?.listings_discovered;
  const showListings =
    !isTerminalStatus(status) && listingsDiscovered !== undefined && listingsDiscovered !== null;
  const truncated = taskDetail?.knowledge_truncated || taskDetail?.listings_truncated;

  return (
    <li className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-white p-3">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span className="mt-0.5 shrink-0 text-gray-500">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-gray-800" title={label}>
              {label}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                isAuto ? "bg-indigo-50 text-indigo-600" : "bg-gray-100 text-gray-600"
              }`}
            >
              {isAuto ? "Auto" : "Manual"}
            </span>
            <span className="text-[11px] uppercase tracking-wide text-gray-400">{meta.label}</span>
          </div>

          {source?.ref && source.ref !== label && (
            <p className="mt-0.5 truncate text-xs text-gray-500" title={source.ref}>
              {source.ref}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <KnowledgeStatusPill status={status} progress={progress} isRetrying={isRetrying} />
            {status === "completed" && source?.byte_size ? (
              <span className="text-xs text-gray-500">{formatBytes(source.byte_size)}</span>
            ) : null}
          </div>

          {showListings && (
            <p className="mt-1 text-xs text-gray-500">
              Scraped {taskDetail.listings_scraped ?? 0} of {listingsDiscovered} listings
              {taskDetail.listings_failed ? ` (${taskDetail.listings_failed} failed)` : ""}
            </p>
          )}

          {truncated && (
            <p className="mt-1 text-xs text-amber-600">
              Knowledge was truncated to fit the compiled size limit.
            </p>
          )}

          {status === "failed" && source?.error && (
            <p className="mt-1 text-xs text-red-500" title={source.error}>
              {source.error}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {canRetry && (
          <button
            type="button"
            onClick={() => onRetry?.(source)}
            disabled={isRetrying}
            className="flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
            title="Retry extraction"
          >
            <IoRefreshOutline className={`h-3.5 w-3.5 ${isRetrying ? "animate-spin" : ""}`} />
            Retry
          </button>
        )}
        <button
          type="button"
          onClick={() => onRemove?.(source)}
          disabled={isRemoving}
          className="border-transparent flex items-center gap-1 rounded border px-2 py-1 text-xs text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50"
          title="Remove source"
        >
          <IoTrashOutline className="h-3.5 w-3.5" />
          Remove
        </button>
      </div>
    </li>
  );
};

export default KnowledgeSourceItem;
